import React, { useState, useEffect, useCallback } from 'react';
import { ServerIcon, TableCellsIcon, ChartBarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { connectionsAPI } from '../../../api/enhancedApiService';

export default function EnhancedOnboarding({ connectionId, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [onboardingData, setOnboardingData] = useState(null);
  const [error, setError] = useState(null);

  const loadOnboardingData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await connectionsAPI.getOnboardingData(connectionId);
      setOnboardingData(response);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    loadOnboardingData();
  }, [loadOnboardingData]);

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            Analyzing your Snowflake environment...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error Loading Onboarding Data
            </h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const getRowCountColor = (count) => {
    if (count > 1000000) return 'text-red-700 bg-red-100';
    if (count > 10000) return 'text-yellow-700 bg-yellow-100';
    return 'text-green-700 bg-green-100';
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Successful!</h2>
          <p className="text-gray-600">
            Your Snowflake connection is working perfectly. Here's what we discovered about your data environment.
          </p>
        </div>

        {/* Summary Statistics */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <TableCellsIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Tables</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {onboardingData.metadata_summary.total_tables}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <ServerIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Columns</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {onboardingData.metadata_summary.total_columns}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Rows</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {onboardingData.metadata_summary.total_rows.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <ServerIcon className="h-8 w-8 text-indigo-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Database</p>
                <p className="text-lg font-semibold text-gray-900">
                  {onboardingData.metadata_summary.database}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Initial Findings */}
      {onboardingData.initial_findings.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Initial Analysis</h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {onboardingData.initial_findings.length} insights
            </span>
          </div>
          <ul className="space-y-3">
            {onboardingData.initial_findings.map((finding, index) => (
              <li key={index} className="flex items-start">
                <CheckCircleIcon className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5" />
                <span className="ml-3 text-gray-700">{finding}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tables Overview */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Tables Discovery</h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {onboardingData.tables.length} tables
          </span>
        </div>
        <p className="text-gray-600 mb-6">
          We found <strong>{onboardingData.tables.length} tables</strong> in your{' '}
          <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
            {onboardingData.database}.{onboardingData.schema}
          </code> schema.
          Here's a preview of your data structure:
        </p>
        
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Table Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Columns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Database
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schema
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {onboardingData.tables.slice(0, 10).map((table, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {table.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {table.column_count} columns
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRowCountColor(table.row_count)}`}>
                      {table.row_count.toLocaleString()} rows
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {table.database}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {table.schema}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            <strong>You're all set!</strong> Your connection is ready for data quality monitoring, 
            validation rules, and anomaly detection.
          </p>
        </div>
      </div>
    </div>
  );
}